import classNames from 'classnames';
import * as qs from 'query-string';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import FiMiniImage from '../../assets/images/plp-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/plp-fi-series-three-plus.png';
import FiUltraImage from '../../assets/images/plp-fi-ultra.png';
import { ReactComponent as ArrowRight } from '../../assets/images/icons/arrow_right.svg';
import { ReactComponent as IconMagic } from '../../assets/images/icons/icon_magic.svg';
import { ultraIsDiscoverable, useLocale } from '../../contexts/LocaleContext';
import { formatHardwarePrice } from '../../lib/hardwareFirst';
import { useIsGrowthBookEnabled } from '../../lib/growthbook';
import useFreeTrialEligibility from '../../hooks/useFreeTrialEligibility';
import useHardwareFirstAddGuard from '../../hooks/useHardwareFirstAddGuard';
import useHardwareFirstTrialOffered from '../../hooks/useHardwareFirstTrialOffered';
import useHardwareFirstShopCardsPricing from '../../hooks/useHardwareFirstShopCardsPricing';
import useShopPromotionOffer from '../../hooks/useShopPromotionOffer';
import { useStickyHeaderOffset } from '../../hooks/useStickyHeaderOffset';
import {
  hardwareFirstProductAdded,
  hardwareFirstProductViewed,
  hardwareFirstTrialToggled,
  productListingPage,
  quizQuestionPage,
  ultraPDPShoppingForPickerShown,
  ultraPDPShoppingForSelected,
} from '../../lib/analytics/events';
import { petTypeForSpecies } from '../../lib/analytics/cartSnapshot';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import { cartActions } from '../../reducers/storeCart';
import * as types from '../../types';
import FreeTrialInfoBanner from '../FreeTrialInfoBanner';
import HardwareFirstTrialToggle from '../HardwareFirstTrialToggle';
import ShopProductsJsonLd from '../ShopProductsJsonLd/ShopProductsJsonLd';
import StarRating from '../StarRating/StarRating';
import Button from '../Button';
import {
  HARDWARE_FIRST_MINI_HERO,
  hardwareFirstLifestyleHero,
  hardwareFirstLineupHero,
  hardwareFirstS3GalleryVariant,
  hardwareFirstS3Hero,
} from './hardwareFirstHeroImages';
import { DesktopImageGallery as MiniImageGallery } from '../MiniPDP/MiniImageGallery/MiniImageGallery';
import UltraDesktopImageGallery from '../UltraPDP/UltraDesktopImageGallery/UltraDesktopImageGallery';
import WhoAreYouShoppingFor, { BuyingFor } from '../UltraPDP/WhoAreYouShoppingFor/WhoAreYouShoppingFor';
import useTrackAddedItemToCart from '../../hooks/useTrackAddedItemToCart';
import useUltraEligiblePets from '../../hooks/useUltraEligiblePets';
import { gallerySlideVideo } from './hardwareFirstVideos';
import { DesktopImageGallery as S3ImageGallery } from '../../views/ProductDetails/Series3CollarDetails/components/ImageGallery';
import { Species } from '../../types/gql-op-types';
import HardwareFirstLearnMoreModal from './HardwareFirstLearnMoreModal';
import HardwareFirstVariantStep from './HardwareFirstVariantStep';
import { SoloBuyBox, SoloCta, SoloFeatures, SoloHeader, SoloPlanCard, SoloTrustRow } from './HardwareFirstSolo';
import useVariantSelector from '../../views/ProductDetails/hooks/useVariantSelector';
import { DeviceQuizAutoOpen } from '../../views/Shop/DeviceQuizRoute';
import HardwareFirstBelowFold, { PickYourFiDeviceKey } from './HardwareFirstBelowFold';
import {
  defaultShopLineup,
  forgetShopLineup,
  productsFromQuery,
  RememberedShopLineupReadableContext,
  rememberedShopLineup,
  rememberShopLineup,
} from './hardwareFirstShopQuery';
import styles from './HardwareFirstPickYourFi.module.scss';
import soloStyles from './HardwareFirstSolo.module.scss';

type DeviceKey = PickYourFiDeviceKey;

// marketing links land on a specific device (/shop?selected=ultra). aliases cover the
// product ids and spellings an ad build is likely to use alongside the short keys.
const DEVICE_QUERY_ALIASES: Record<string, DeviceKey> = {
  mini: 'mini',
  'fi-mini': 'mini',
  s3: 's3',
  's3-plus': 's3',
  series3: 's3',
  'series-3': 's3',
  'series-3-plus': 's3',
  'smart-collar-v3b': 's3',
  ultra: 'ultra',
  'fi-ultra': 'ultra',
};

function deviceFromQuery(search: string): DeviceKey | undefined {
  const requested = qs.parse(search).selected;
  return typeof requested === 'string' ? DEVICE_QUERY_ALIASES[requested.trim().toLowerCase()] : undefined;
}

/**
 * `?type=cat` puts the page in its cats treatment: mini is the only cat tracker, so the device
 * cards collapse to one and the copy, gallery and below-fold sections all switch. The param name
 * matches the one the standalone pdps already read, so a cats link works on either page.
 */
function speciesFromQuery(search: string): Species {
  return qs.parse(search).type === 'cat' ? Species.CAT : Species.DOG;
}

/** Rewrites only `selected` and drops `step`, so a landing url keeps its utm and coupon params. */
function searchWithDevice(search: string, device: DeviceKey): string {
  const { step, ...rest } = qs.parse(search);
  return qs.stringify({ ...rest, selected: device });
}

interface DeviceCard {
  key: DeviceKey;
  name: string;
  image: string;
  thumb: string;
  description: string;
  pdpPath: string;
  /** sku added to the cart (and priced for display); undefined = the device needs its PDP (variant choice). */
  addSku?: string;
  /** sku used only to price the card when addSku is undefined (any variant of the product). */
  pricingSku?: string;
  trackShopNow: () => void;
  trackLearnMore: () => void;
}

/**
 * whether the card quotes the live promotion's price under its own: only while nothing is taken off
 * yet, and only once the code is known to reprice this tracker; a code the server does not honor
 * for it prices at list and stays quiet
 */
function offerPillShown(
  discount: types.HardwareDiscountPreview | undefined,
  offer: types.HardwareDiscountPreview | undefined,
): boolean {
  return (
    !!discount &&
    discount.listPriceInCents <= discount.discountedPriceInCents &&
    !!offer &&
    offer.promoDiscountInCents > 0
  );
}

function CardPrice({
  discount,
  offer,
  freeTrial,
}: {
  discount: types.HardwareDiscountPreview | undefined;
  /** the same tracker priced with the live promotion's code, while the shopper has not applied it */
  offer?: types.HardwareDiscountPreview;
  /** the free trial is on for this card: the price reads "free for 30 days" */
  freeTrial?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (!discount) return null;
  if (freeTrial) {
    return (
      <div className={styles.cardPriceBlock}>
        <span className={styles.trialPill}>{t('hardware_first_trial_free_for_30_days')}</span>
      </div>
    );
  }
  const savedInCents = discount.listPriceInCents - discount.discountedPriceInCents;
  const showOffer = offerPillShown(discount, offer) && !!offer;
  return (
    <div className={styles.cardPriceBlock}>
      <div className={styles.cardPrice}>
        <span className={styles.discountedPrice}>{formatHardwarePrice(discount.discountedPriceInCents, locale)}</span>
        {savedInCents > 0 && (
          <span className={styles.listPrice}>{formatHardwarePrice(discount.listPriceInCents, locale)}</span>
        )}
      </div>
      {savedInCents > 0 && (
        <span className={styles.savedPill}>
          {t('hardware_first_card_saved', { amount: formatHardwarePrice(savedInCents, locale) })}
        </span>
      )}
      {showOffer && (
        <span className={styles.offerPill}>
          {t('hardware_first_card_price_with_promo', {
            // the pill is a teaser, not the quote: whole dollars, the exact price lands once the code is on
            price: locale.wholeNumberCurrencyFormatter.format(Math.round(offer.discountedPriceInCents / 100)),
          })}
        </span>
      )}
    </div>
  );
}

/**
 * Hardware-first replacement for the product listing page: a single "Pick your Fi" selection
 * surface with server-priced hardware cards and a one-time-hardware-price add-to-cart bar.
 * Rendered only for arm-enrolled d2c sessions; control keeps the classic PLP.
 */
// the fi-quizz widget only recommends mini or s3+
const QUIZ_DEVICE_KEYS: readonly DeviceKey[] = ['mini', 's3'];

export default function HardwareFirstPickYourFi() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const launchQuiz = useLaunchQuiz();
  const locale = useLocale();
  const { country, currency } = locale;
  const { atCap, routeToBag } = useHardwareFirstAddGuard();
  const trackAddedItemToCart = useTrackAddedItemToCart();
  const products = useSelector((state: types.AppState) => state.config.products);
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);
  // ultra is country- and policy-gated exactly as it is on the classic plp; without this
  // an unsupported market could add an lte/satellite device straight to the bag
  const isUltraAvailable = useIsGrowthBookEnabled() && ultraIsDiscoverable(country, internalTester);
  const loggedIn = useSelector((state: types.AppState) => !!state.session);
  const { pets: eligiblePets } = useUltraEligiblePets();
  const species = speciesFromQuery(location.search);
  const isCats = species === Species.CAT;
  // a landing link's lineup outlives its url: the bag's back button and the nav come back to bare
  // /shop and the shopper should see the same trackers. an explicit `selected=` outside the
  // remembered lineup is the shopper stepping out of it (a nav tap on a hidden tracker), so it wins.
  // with neither, the shop opens without ultra unless the link selected it.
  const urlProducts = productsFromQuery(location.search);
  const lineupReadable = useContext(RememberedShopLineupReadableContext);
  const remembered = lineupReadable ? rememberedShopLineup() : undefined;
  const urlSelected = deviceFromQuery(location.search);
  const leavesLineup = !urlProducts && !!remembered && !!urlSelected && !remembered.includes(urlSelected);
  const requestedProducts = urlProducts ?? (leavesLineup ? undefined : (remembered ?? defaultShopLineup(urlSelected)));
  useEffect(() => {
    if (leavesLineup) {
      forgetShopLineup();
    } else {
      rememberShopLineup(location.search);
    }
  }, [location.search, leavesLineup]);
  // mini is preselected so the footer price and below-fold have content on first paint; a
  // ?selected= link overrides it once, on mount, after which the shopper's taps own the choice
  const [selectedKey, setSelectedKey] = useState<DeviceKey>(() => deviceFromQuery(location.search) ?? 'mini');
  const [trialOn, setTrialOn] = useState(false);
  // a nav tap while already on /shop only rewrites `selected=`; the page stays mounted, so the new
  // device has to be picked up here. the card taps write the url too, but they set the key first,
  // so the two already agree and nothing moves.
  const selectedKeyRef = useRef(selectedKey);
  selectedKeyRef.current = selectedKey;
  useEffect(() => {
    if (!urlSelected || urlSelected === selectedKeyRef.current) {
      return;
    }
    setSelectedKey(urlSelected);
    window.scrollTo({ top: 0 });
  }, [urlSelected]);

  // mirror the choice into the url so a refresh or a shared link keeps it. `replace`, not
  // `push`: pushing would turn the back button into a device-selection rewind.
  const selectDevice = useCallback(
    (device: DeviceKey) => {
      setSelectedKey(device);
      history.replace({ search: searchWithDevice(location.search, device) });
    },
    [history, location.search],
  );

  const miniProduct = products.find((p) => p.id === types.miniCollarId) as types.IMiniProduct | undefined;
  const s3Product = products.find((p) => p.id === types.series3PlusCollarId) as types.ICollarKitProduct | undefined;
  const ultraProduct = products.find((p) => p.id === types.ultraCollarId) as types.IUltraProduct | undefined;

  const devices: DeviceCard[] = [
    {
      key: 'mini',
      name: 'Fi Mini',
      image: FiMiniImage,
      thumb: '/product_images/mini/device.png',
      description: t('hardware_first_description_mini'),
      pdpPath: AppPaths.Products('fi-mini') + '?type=dog',
      addSku: miniProduct?.sku,
      trackShopNow: productListingPage.miniShopNow,
      trackLearnMore: productListingPage.miniLearnMore,
    },
    {
      key: 's3',
      name: 'Fi Series 3+',
      image: FiSeries3PlusImage,
      thumb: '/product_images/smart-collar-v3b/gray.png',
      description: t('hardware_first_description_s3'),
      pdpPath: AppPaths.Products(types.series3PlusCollarId),
      // band size/color live on the PDP, so adding routes there instead of straight to the bag
      pricingSku: s3Product?.variants[0]?.sku,
      trackShopNow: productListingPage.series3PlusShopNow,
      trackLearnMore: productListingPage.series3PlusLearnMore,
    },
    {
      key: 'ultra',
      name: 'Fi Ultra',
      image: FiUltraImage,
      thumb: '/product_images/smart-collar-ultra.png',
      description: t('hardware_first_description_ultra'),
      pdpPath: AppPaths.Products('fi-ultra'),
      addSku: ultraProduct?.sku,
      trackShopNow: productListingPage.ultraShopNow,
      trackLearnMore: productListingPage.ultraLearnMore,
    },
  ];

  // the cards quote the applied code's price; a promotion the shopper has not applied quotes list,
  // and while that promotion's code is not on the cart each card also quotes its price under it
  const offerCode = useShopPromotionOffer();
  const skusByKey: Record<DeviceKey, string | undefined> = {
    mini: devices[0].addSku,
    s3: devices[1].pricingSku,
    ultra: isUltraAvailable ? devices[2].addSku : undefined,
  };
  const { discounts, offers } = useHardwareFirstShopCardsPricing(Object.values(skusByKey), offerCode);
  const pricedFor = (bySku: typeof discounts, key: DeviceKey) => {
    const sku = skusByKey[key];
    return sku ? bySku[sku] : undefined;
  };
  const discountsByKey: Record<DeviceKey, types.HardwareDiscountPreview | undefined> = {
    mini: pricedFor(discounts, 'mini'),
    s3: pricedFor(discounts, 's3'),
    ultra: pricedFor(discounts, 'ultra'),
  };
  const offersByKey: Record<DeviceKey, types.HardwareDiscountPreview | undefined> = {
    mini: pricedFor(offers, 'mini'),
    s3: pricedFor(offers, 's3'),
    ultra: pricedFor(offers, 'ultra'),
  };

  const s3Variants = s3Product?.variants ?? [];
  const noop = () => undefined;
  const { changeVariantOptions, selectedVariant, hasUpdatedSize } = useVariantSelector({
    onColorChange: noop,
    onSelectedVariantChange: noop,
    onSizeChange: noop,
    variants: s3Variants,
    useQueryParams: false,
  });

  // a catalog without s3+ drops its card, as ultra's is dropped where ultra isn't sold. before the
  // catalog loads (main's prerendered document) the card stays, so the document carries the lineup
  const isSoldHere = (device: DeviceCard) =>
    (device.key !== 'ultra' || isUltraAvailable) &&
    (device.key !== 's3' || s3Variants.length > 0 || products.length === 0);
  const gatedDevices = isCats ? devices.filter((device) => device.key === 'mini') : devices.filter(isSoldHere);
  // a `products=` link narrows the lineup to the trackers it names. one that names nothing this
  // market can show falls back to the full lineup rather than an empty page.
  const requestedDevices = requestedProducts
    ? gatedDevices.filter((device) => requestedProducts.includes(device.key))
    : gatedDevices;
  const visibleDevices = requestedDevices.length > 0 ? requestedDevices : gatedDevices;
  // a one-tracker lineup renders as a product page instead of a one-card list
  const solo = visibleDevices.length === 1;
  const s3GalleryVariant = hardwareFirstS3GalleryVariant(solo, selectedVariant, s3Variants);
  // a narrowed page loses the "three trackers" subtitle: the link already chose
  const lineupNarrowed = visibleDevices.length < gatedDevices.length;
  // the quiz picks between mini and s3+, so a narrowed page keeps it only while both are on it. a
  // lineup missing either would let the quiz recommend a tracker the page is hiding.
  const quizAvailable =
    !lineupNarrowed || QUIZ_DEVICE_KEYS.every((key) => visibleDevices.some((device) => device.key === key));
  const selected = visibleDevices.find((d) => d.key === selectedKey) ?? visibleDevices[0];
  const selectedName = isCats ? t('hardware_first_mini_for_cats') : selected.name;

  // a signed-in member with a dog on an eligible membership can add an ultra to it instead of
  // starting a new one. the choice rides the cart item so the order records which purchase it was;
  // the price is the same either way. everyone else buys standalone without being asked.
  const showUltraPicker = selected.key === 'ultra' && loggedIn && eligiblePets.length > 0;
  const [buyingFor, setBuyingFor] = useState<BuyingFor>('existing');
  const ultraAttachment = showUltraPicker && buyingFor === 'existing';
  useEffect(() => {
    if (showUltraPicker) {
      ultraPDPShoppingForPickerShown({ eligiblePetCount: eligiblePets.length });
    }
  }, [showUltraPicker, eligiblePets.length]);
  const onSelectBuyingFor = (choice: BuyingFor) => {
    setBuyingFor(choice);
    ultraPDPShoppingForSelected({ choice, eligiblePetCount: eligiblePets.length });
  };
  const selectedDiscount = discountsByKey[selected.key];

  // the 30-day trial is a legacy trial bundle (collar + its 12-month trial plan) for an eligible mini or
  // s3+. one switch covers both trackers and sits in its own card under the trackers.
  const hardwareFirstTrial = useHardwareFirstTrialOffered();
  const miniTrial = useFreeTrialEligibility(types.miniCollarId, { includeHardwareFirst: true });
  const s3Trial = useFreeTrialEligibility(types.series3PlusCollarId, { includeHardwareFirst: true });
  const trialPlanFor = (key: DeviceKey) => {
    const productId = key === 'mini' ? types.miniCollarId : key === 's3' ? types.series3PlusCollarId : undefined;
    const eligible = key === 'mini' ? miniTrial.isEligible : s3Trial.isEligible;
    return hardwareFirstTrial && eligible && productId
      ? products.find(
          (p): p is types.ISubscriptionProduct =>
            types.isModuleSubscriptionProduct(p) &&
            p.freeTrial === true &&
            p.renewalMonths === 12 &&
            p.canBePurchasedForProductIds.includes(productId),
        )
      : undefined;
  };
  const selectedTrialPlan = trialPlanFor(selected.key);
  const trialSelected = trialOn && !!selectedTrialPlan;
  // the switch is per tracker: it turns off on every tracker change (card, quiz or url) and when
  // the selected tracker has no trial to offer
  useEffect(() => {
    setTrialOn(false);
  }, [selected.key]);
  useEffect(() => {
    if (!selectedTrialPlan) setTrialOn(false);
  }, [selectedTrialPlan]);
  const addLabel = t(trialSelected ? 'start_free_trial' : 'hardware_first_add_to_cart');
  // a trial already in the cart replaces the switch with the reason
  const selectedTrialRestriction =
    selected.key === 'mini' ? miniTrial.restrictionReason : selected.key === 's3' ? s3Trial.restrictionReason : null;
  const trialControl = selectedTrialPlan ? (
    <HardwareFirstTrialToggle
      checked={trialOn}
      onChange={(enabled) => {
        setTrialOn(enabled);
        hardwareFirstTrialToggled({ enabled, device: selected.key === 'mini' ? 'mini' : 's3' });
      }}
      productCategory={selected.key === 'mini' ? types.ProductCategory.MINI : types.ProductCategory.COLLAR_KIT}
      listPriceInCents={selectedDiscount?.listPriceInCents}
    />
  ) : (
    hardwareFirstTrial &&
    selectedTrialRestriction === 'in_cart' && <FreeTrialInfoBanner restrictionReason={selectedTrialRestriction} />
  );

  // the selected card is the product page here, so it reports a view with the price it shows.
  // fires once the preview lands (the card has no price row before that), again when a code
  // reprices it, and, for the s3+, again for each band colour or size picked, matching the pdps.
  // the price stays the card's quote, which is what the shopper sees for whichever band is picked.
  // the s3+ waits for the default band to resolve: falling back to the first variant meanwhile
  // would report two views on load when the preview is already cached.
  const viewedSku = selected.key === 's3' ? selectedVariant?.sku : selected.addSku;
  // keyed on the quoted price, not the preview object: a refetch hands back an equal quote as a new
  // object, which would report the same view twice.
  const viewedPriceInCents = selectedDiscount?.discountedPriceInCents;
  const viewedDiscount = useRef(selectedDiscount);
  viewedDiscount.current = selectedDiscount;
  useEffect(() => {
    if (viewedSku && viewedDiscount.current) {
      hardwareFirstProductViewed({ sku: viewedSku, currency, discount: viewedDiscount.current });
    }
  }, [viewedSku, viewedPriceInCents, currency]);

  // the s3+ pickers sit inline (on the solo page, and in the lineup's s3+ card), so its cta only
  // ever asks for the size
  const s3StepNeedsSize = selected.key === 's3' && !hasUpdatedSize;
  // main's prerendered shop is on screen before the catalog loads, and the page mounts again once it
  // does, so a tap before then would open an empty size step or leave for a pdp, and then be lost
  const catalogLoaded = products.length > 0;
  const ctaLabel = s3StepNeedsSize ? t('pdp_v2_select_a_size') : addLabel;

  // the hero column stays put while the rail scrolls, so the tracker is in front of the shopper
  // for the whole decision. the app bar is sticky and its height changes with the promotion
  // banner and the scroll collapse, so the offset is measured rather than hardcoded.
  const heroRef = useRef<HTMLDivElement>(null);
  useStickyHeaderOffset(heroRef, '--hf-sticky-top');
  // the page reads the same offset to size the square gallery and centre the columns
  const pageRef = useRef<HTMLDivElement>(null);
  useStickyHeaderOffset(pageRef, '--hf-sticky-top');

  const ctaRef = useRef<HTMLDivElement>(null);
  // the s3+ cta reads `Select a size` until one is picked. it stays tappable: a tap
  // scrolls the size chips into view, rather than doing nothing
  const sizePickersRef = useRef<HTMLDivElement>(null);
  const [ctaPassed, setCtaPassed] = useState(false);
  useEffect(() => {
    const target = ctaRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      setCtaPassed(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      setCtaPassed(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [selected.key]);
  // the desktop bar waits until the shopper has scrolled past the page's own cta, rather
  // than showing while that cta is still below the fold
  const footerHidden = !ctaPassed;

  // the shop opens at the top of the page
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onQuiz = () => {
    quizQuestionPage.quizButtonTapped();
    launchQuiz();
  };

  const onAddToCart = () => {
    if (!catalogLoaded) {
      return;
    }
    if (s3StepNeedsSize) {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      sizePickersRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      return;
    }
    selected.trackShopNow();
    const sku = selected.key === 's3' ? selectedVariant?.sku : selected.addSku;
    if (!sku) {
      history.push(selected.pdpPath);
      return;
    }
    // at the unit cap, skip the add the server would reject; the bag's bulk-sales notice explains.
    // a trial bundle is not a counted hardware unit, so the cap does not apply to it.
    const trialPlan = trialSelected ? selectedTrialPlan : undefined;
    if (trialPlan || !atCap) {
      // web rejects a gift with a trial
      if (trialPlan) dispatch(cartActions.setIsGift(false));
      dispatch(
        trialPlan
          ? cartActions.addBundleCartItem({
              sku,
              subscriptionSku: trialPlan.sku,
              addonSkus: [],
              currency,
              isGift: false,
              species,
            })
          : cartActions.addSingleLineCartItem({ sku, currency, ...(selected.key === 'ultra' && { ultraAttachment }) }),
      );
      // a trial reports the catalog price, like the legacy trial add, not the $0 it costs today
      hardwareFirstProductAdded({
        sku,
        currency,
        source: 'hardware_first_plp',
        discount: trialPlan ? undefined : selectedDiscount,
      });
      void trackAddedItemToCart(sku, petTypeForSpecies(species));
    }
    routeToBag();
  };

  // the solo page and the lineup both open on a lifestyle shot, so the hero and the tracker card do
  // not show the same render
  const lifestyleHero = solo
    ? hardwareFirstLifestyleHero(selected.key, species)
    : hardwareFirstLineupHero(selected.key, species);
  // the video rides along as the last gallery slide; cats have no footage of their own
  const gallerySlides = useMemo(
    () => (species === Species.CAT ? undefined : [gallerySlideVideo(selected.key)]),
    [species, selected.key],
  );

  // the trial card sits right above the cta it changes
  const trialCta = (
    <>
      {trialControl && <div className={soloStyles.trialSlot}>{trialControl}</div>}
      <SoloCta label={ctaLabel} disabled={!catalogLoaded} onClick={onAddToCart} ctaRef={ctaRef} />
    </>
  );

  return (
    <div className={`${styles.pageBackground} hardware-first-plp`}>
      <div className={classNames(styles.page, soloStyles.pageSolo)} ref={pageRef}>
        <div className={classNames(styles.layout, soloStyles.layoutSolo)}>
          <div className={classNames(styles.hero, soloStyles.heroSolo)} ref={heroRef}>
            {/* the lineup names the tracker on its photo, since its heading is the chooser's */}
            {!solo && <span className={soloStyles.galleryPill}>{selectedName}</span>}
            <div className={styles.gallery}>
              {selected.key === 'mini' && (
                <MiniImageGallery
                  heroImage={lifestyleHero ?? HARDWARE_FIRST_MINI_HERO}
                  species={species}
                  prevNextButtons
                  appendMedia={gallerySlides}
                  animateThumbnailSelect
                />
              )}
              {selected.key === 's3' && s3GalleryVariant && (
                <S3ImageGallery
                  heroImage={lifestyleHero ?? hardwareFirstS3Hero(s3Product?.variants[0])}
                  selectedVariant={s3GalleryVariant}
                  prevNextButtons
                  appendMedia={gallerySlides}
                  animateThumbnailSelect
                />
              )}
              {selected.key === 'ultra' && (
                <UltraDesktopImageGallery
                  heroImage={lifestyleHero}
                  prevNextButtons
                  appendMedia={gallerySlides}
                  animateThumbnailSelect
                />
              )}
            </div>
          </div>
          <div className={classNames(styles.rail, soloStyles.railV2)}>
            <div className={classNames(styles.headerCopy, { [soloStyles.lineupHeader]: !solo })}>
              {solo ? (
                <SoloHeader
                  name={selectedName}
                  lede={
                    isCats
                      ? t('product_description_mini_cats')
                      : // the solo ultra page leads with ultra's tagline; the lineup card keeps its longer pitch
                        selected.key === 'ultra'
                        ? t('compare_v2_tagline_ultra')
                        : selected.description
                  }
                  discount={selectedDiscount}
                  offer={offersByKey[selected.key]}
                  freeTrial={trialSelected}
                  timeAward={selected.key === 'ultra'}
                />
              ) : (
                <>
                  <div className={soloStyles.starRow}>
                    <StarRating />
                  </div>
                  {/* the heading names the tracker in the gallery, so on mobile, where the cards sit below
                      it, the shopper reads which one they are looking at without scrolling back down. */}
                  <h1 className={classNames(styles.title, soloStyles.title)}>{selectedName}</h1>
                </>
              )}
              <ShopProductsJsonLd
                products={devices.map(({ key, name, thumb, description, addSku, pricingSku }) => {
                  // listings use the product line's own one-liners: the mini card on the cats page is
                  // "Fi Mini for cats", and ultra's listing copy is not its card's off-leash pitch
                  const isCatMini = isCats && key === 'mini';
                  const listingDescription = isCatMini
                    ? t('product_description_mini_cats')
                    : key === 'ultra'
                      ? t('product_description_ultra')
                      : description;
                  return {
                    key,
                    name: isCatMini ? t('hardware_first_mini_for_cats') : name,
                    thumb,
                    description: listingDescription,
                    sku: addSku ?? pricingSku,
                  };
                })}
                discountsByKey={discountsByKey}
              />
              {/* the quiz compares mini and s3+; a lineup without both has already decided */}
              {quizAvailable && !solo && (
                <button className={soloStyles.quizRow} onClick={onQuiz} type="button">
                  <IconMagic aria-hidden="true" className={soloStyles.quizRowIcon} />
                  <span className={soloStyles.quizRowText}>
                    <b>{t('pdp_v2_quiz_row_prompt')}</b> {t('pdp_v2_quiz_row_cta')}
                  </span>
                  <ArrowRight aria-hidden="true" className={soloStyles.quizRowArrow} />
                </button>
              )}
            </div>
            {solo && <SoloFeatures deviceKey={selected.key} />}
            {showUltraPicker && (
              <WhoAreYouShoppingFor
                petNames={eligiblePets.map((pet) => pet.name)}
                selected={buyingFor}
                onSelect={onSelectBuyingFor}
              />
            )}
            {solo ? (
              selected.key === 's3' && selectedVariant ? (
                // s3+ picks its band size and color right on the page, no step to open
                <SoloBuyBox>
                  <div ref={sizePickersRef}>
                    <HardwareFirstVariantStep
                      changeVariantOptions={changeVariantOptions}
                      hasUpdatedSize={hasUpdatedSize}
                      selectedVariant={selectedVariant}
                      variants={s3Variants}
                      compact
                    />
                  </div>
                  {trialCta}
                </SoloBuyBox>
              ) : (
                trialCta
              )
            ) : (
              visibleDevices.map((device) => (
                <div
                  key={device.key}
                  className={classNames(styles.card, styles.cardV2, {
                    [styles.cardSelected]: device.key === selected.key,
                  })}
                  onClick={() => selectDevice(device.key)}
                >
                  <div
                    className={classNames(styles.cardHeader, {
                      [styles.cardHeaderOffer]: offerPillShown(discountsByKey[device.key], offersByKey[device.key]),
                    })}
                  >
                    <img className={styles.cardThumb} src={device.thumb} alt={device.name} />
                    <div className={styles.cardName}>{device.name}</div>
                    <CardPrice
                      discount={discountsByKey[device.key]}
                      offer={offersByKey[device.key]}
                      freeTrial={trialSelected && device.key === selected.key}
                    />
                  </div>
                  <div className={styles.cardDivider} />
                  <div className={styles.cardDescription}>{device.description}</div>
                  {device.key !== selected.key && trialPlanFor(device.key) && (
                    <div className={styles.cardTrialNote}>{t('hardware_first_trial_available')}</div>
                  )}
                  <HardwareFirstLearnMoreModal
                    deviceKey={device.key}
                    species={species}
                    name={device.name}
                    discount={discountsByKey[device.key]}
                    onOpen={device.trackLearnMore}
                    onSelect={() => selectDevice(device.key)}
                    trigger={
                      <button className={styles.learnMore} type="button">
                        {t('learn_more')}
                      </button>
                    }
                  />
                  {device.key === 's3' && selected.key === 's3' && selectedVariant && (
                    // the pickers sit in the chosen card; taps on them must not re-run the card's select
                    <div
                      className={styles.cardPickers}
                      onClick={(event) => event.stopPropagation()}
                      ref={sizePickersRef}
                    >
                      <HardwareFirstVariantStep
                        changeVariantOptions={changeVariantOptions}
                        hasUpdatedSize={hasUpdatedSize}
                        selectedVariant={selectedVariant}
                        variants={s3Variants}
                        compact
                      />
                    </div>
                  )}
                </div>
              ))
            )}
            {!solo && trialCta}
            {!ultraAttachment && <SoloPlanCard deviceName={selectedName} />}
            <SoloTrustRow />
          </div>
        </div>
        <DeviceQuizAutoOpen available={quizAvailable} />
        <HardwareFirstBelowFold deviceKey={selected.key} species={species} showQuiz={quizAvailable} />
        <div className={classNames(styles.footer, styles.footerV2, { [styles.footerHidden]: footerHidden })}>
          <div className={styles.footerInner}>
            {selectedDiscount && (
              <div className={styles.footerPriceRow}>
                <span className={styles.footerLabel}>
                  {trialSelected ? t('hardware_first_trial_footer_label', { name: selected.name }) : selected.name}
                </span>
                <span className={styles.footerPrice}>
                  {trialSelected ? (
                    <span className={styles.discountedPrice}>{t('hardware_first_trial_free_for_30_days')}</span>
                  ) : (
                    <>
                      {selectedDiscount.listPriceInCents > selectedDiscount.discountedPriceInCents && (
                        <span className={styles.offPill}>
                          {selectedDiscount.promoDiscountInCents > 0
                            ? t('hardware_first_percent_off', {
                                percent: Math.round(
                                  (selectedDiscount.promoDiscountInCents / selectedDiscount.listPriceInCents) * 100,
                                ),
                              })
                            : t('hardware_first_amount_off', {
                                amount: formatHardwarePrice(
                                  selectedDiscount.listPriceInCents - selectedDiscount.discountedPriceInCents,
                                  locale,
                                ),
                              })}
                        </span>
                      )}
                      <span className={styles.discountedPrice}>
                        {formatHardwarePrice(selectedDiscount.discountedPriceInCents, locale)}
                      </span>
                      {selectedDiscount.listPriceInCents > selectedDiscount.discountedPriceInCents && (
                        <span className={styles.listPrice}>
                          {formatHardwarePrice(selectedDiscount.listPriceInCents, locale)}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </div>
            )}
            <Button className={styles.addToCart} disabled={!catalogLoaded} onClick={onAddToCart}>
              {ctaLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
